'use client'
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import type { ReactNode } from 'react'
import { useShopifyInsightsAccess, type ShopifyInsightsMode } from '@/hooks/use-shopify-insights-access'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Loader2, Lock } from 'lucide-react'
import { useTranslations } from "next-intl"


/**
 * Gate wrapper for Shopify Insights pages (cross-sell, economics, bundle rules).
 * Starts trial on first access, shows locked state when expired. The dashboard
 * resolves access itself so revenue and orders stay open without Insights.
 */
export function ShopifyInsightsGate({ children }: { children: ReactNode }) {
  const { allowed, mode, trialExpiresAt, loading } = useShopifyInsightsAccess(true)

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!allowed) return <InsightsLockedNotice mode={mode} />

  return (
    <>
      <InsightsTrialBanner mode={mode} trialExpiresAt={trialExpiresAt} />
      {children}
    </>
  )
}

export function InsightsLockedNotice({ mode }: { mode: ShopifyInsightsMode }) {
  return (
    <div className="flex items-center justify-center py-16 px-6">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <LockedMessage mode={mode} />
      </div>
    </div>
  )
}

/** Only finite trials have an end to announce. */
export function InsightsTrialBanner({ mode, trialExpiresAt }: { mode: ShopifyInsightsMode; trialExpiresAt: string | null }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Shopify.ShopifyShopifyInsightsGate")

  if (mode !== 'trial_active' || !trialExpiresAt) return null

  return (
    <Alert className="mb-4">
      <AlertDescription>
        <span><span key={tUi("shopifyInsightsTrialEndsOn")}>{tUi("shopifyInsightsTrialEndsOn")}</span></span> {new Date(trialExpiresAt).toLocaleDateString(uiFormattingLocale)}<span><span key={tUi("upgradeYourPlanToKeepAccessAfterThe")}>{tUi("upgradeYourPlanToKeepAccessAfterThe")}</span></span>
      </AlertDescription>
    </Alert>
  )
}

function LockedMessage({ mode }: { mode: ShopifyInsightsMode }) {
  const tUi = useTranslations("Shopify.ShopifyShopifyInsightsGate")
  const tCommonUi = useTranslations("Common")

  switch (mode) {
    case 'error':
      return (
        <>
          <h2 className="text-xl font-semibold mb-2"><span key={tUi("unableToLoadShopifyInsights")}>{tUi("unableToLoadShopifyInsights")}</span></h2>
          <p className="text-muted-foreground mb-4">
            <span key={tUi("somethingWentWrongWhileCheckingAccessPleaseTry")}>{tUi("somethingWentWrongWhileCheckingAccessPleaseTry")}</span></p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            <span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
        </>
      )
    case 'forbidden':
      return (
        <>
          <h2 className="text-xl font-semibold mb-2"><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></h2>
          <p className="text-muted-foreground mb-4">
            <span key={tUi("youDonTHavePermissionToAccessShopify")}>{tUi("youDonTHavePermissionToAccessShopify")}</span></p>
        </>
      )
    case 'trial_expired':
      return (
        <>
          <h2 className="text-xl font-semibold mb-2"><span key={tUi("shopifyInsightsTrialExpired")}>{tUi("shopifyInsightsTrialExpired")}</span></h2>
          <p className="text-muted-foreground mb-4">
            <span key={tUi("revenueAndOrdersRemainAvailableButChannelAnalytics")}>{tUi("revenueAndOrdersRemainAvailableButChannelAnalytics")}</span></p>
          <BillingButton />
        </>
      )
    case 'trial_not_started':
      return (
        <>
          <h2 className="text-xl font-semibold mb-2"><span key={tUi("shopifyInsightsTrialNotStarted")}>{tUi("shopifyInsightsTrialNotStarted")}</span></h2>
          <p className="text-muted-foreground mb-4">
            <span key={tUi("theTrialStartsWhenAMemberOfThis")}>{tUi("theTrialStartsWhenAMemberOfThis")}</span></p>
        </>
      )
    case 'not_available':
    case 'unlimited':
    case 'trial_active':
      return (
        <>
          <h2 className="text-xl font-semibold mb-2"><span key={tUi("shopifyInsightsNotAvailable")}>{tUi("shopifyInsightsNotAvailable")}</span></h2>
          <p className="text-muted-foreground mb-4">
            <span key={tUi("yourCurrentPlanDoesNotIncludeShopifyInsights")}>{tUi("yourCurrentPlanDoesNotIncludeShopifyInsights")}</span></p>
          <BillingButton />
        </>
      )
  }
}

function BillingButton() {
  const tUi = useTranslations("Shopify.ShopifyShopifyInsightsGate")

  return (
    <Button onClick={() => { window.location.href = '/?page=billing' }}>
      <span key={tUi("goToBilling")}>{tUi("goToBilling")}</span></Button>
  )
}
